/**
 * **How the Chart tab is sized** (WC1–WC4, 2026-10-28) — the tooth chart takes
 * the whole tab, and a picked tooth's editor opens as a panel over one side of it.
 *
 * Anthony: *"extend the tooth chart to take the entire width of the screen … I
 * think it's too small"*, with *Last visit* as a popup button. Agreed on a
 * prototype (Artifact "Wide Chart Proposed") before any of this was written. It
 * replaces PF13's band under the chart and PF14's column beside it.
 *
 * Pure arithmetic, so it is tested without a browser; the components measure
 * and draw, and ask this what to do.
 */

/**
 * The zoom the teeth may be drawn at. Below 0.7 a tooth is too small a target;
 * 4 lets a 1920 × 1080 screen fill its width (WC1), where PF13's 2 stopped the
 * teeth at under two thirds of it.
 */
export const MIN_TEETH_SCALE = 0.7;
export const MAX_TEETH_SCALE = 4;

/**
 * The tooth panel's width: this share of the tab's width, within these bounds —
 * room for the editor's adder at full width, while leaving most of the teeth in
 * view.
 */
export const SIDE_SHARE = 0.42;
export const MIN_SIDE = 380;
export const MAX_SIDE = 520;

/** The tooth panel's width for a tab this wide (WC2). */
export function panelWidth(tabWidth: number): number {
  return Math.round(Math.min(MAX_SIDE, Math.max(MIN_SIDE, tabWidth * SIDE_SHARE)));
}

/**
 * **The panel opens on the side away from its tooth** (WC2), so the tooth stays
 * in view. Quadrants 2 and 3 — and the baby teeth in front of them, 6 and 7 —
 * are drawn on the right of the chart, so their panel docks on the left.
 */
export function panelDock(fdiNumber: number): "left" | "right" {
  return [2, 3, 6, 7].includes(Math.floor(fdiNumber / 10)) ? "left" : "right";
}

/** What the chart measured: `ChartNeeds` in `tooth-chart.tsx`. */
export interface ChartMeasure {
  /** The rows' height and width at zoom 1. */
  rowsHeight: number;
  rowsWidth: number;
  /** The rest of the card: padding, legend and borders, down and across. */
  chrome: number;
  chromeX: number;
}

/**
 * The zoom the teeth are drawn at: the largest that fits the box both ways,
 * within the limits, rounded down to a hundredth so it never overshoots.
 */
export function teethScale({
  box,
  natural,
}: {
  box: { width: number; height: number };
  natural: { width: number; height: number };
}): number {
  if (natural.width <= 0 || natural.height <= 0) return 1;
  const fits = Math.min(box.width / natural.width, box.height / natural.height);
  const clamped = Math.min(MAX_TEETH_SCALE, Math.max(MIN_TEETH_SCALE, fits));
  return Math.floor(clamped * 100) / 100;
}
